import { X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useState } from 'react'
import { cn } from '#lib/utils'

/*
 * Aceternity UI sticky banner (ui.aceternity.com/components/sticky-banner),
 * adapted:
 * - no hide-on-scroll: in this shell the page scrolls inside its sheet, and a
 *   banner collapsing mid-scroll would shift everything under the cursor. It
 *   sits in the layout above the page and gives its height back when closed;
 * - it opens by growing from zero height, so arriving after the page (a late
 *   fetch) pushes the content down smoothly instead of jumping it;
 * - closing dismisses it for as long as its owner stays mounted, instead of
 *   the demo's toggle;
 * - a labelled button with a lucide icon, theme tokens left to the caller.
 */
export function StickyBanner(props: {
	children: ReactNode
	closeLabel: string
	className?: string
}) {
	const [open, setOpen] = useState(true)

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					animate={{ height: 'auto', opacity: 1 }}
					className='shrink-0 overflow-hidden'
					exit={{ height: 0, opacity: 0 }}
					initial={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
				>
					<div
						className={cn(
							'relative flex min-h-10 items-center gap-2 px-4 py-2 pr-12 text-sm',
							props.className
						)}
					>
						{props.children}
						<button
							aria-label={props.closeLabel}
							className='absolute top-1/2 right-2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md opacity-70 outline-none transition hover:bg-current/10 hover:opacity-100 focus-visible:ring-3 focus-visible:ring-ring/50'
							onClick={() => setOpen(false)}
							type='button'
						>
							<X className='size-4' />
						</button>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
